import React from "react";
import processAdapterConfig from "./processAdapterConfig";

/**
 * A higher-order component which takes a bind config and a "normal" component
 * to bind. There are several ways of expressing a bind config. Here are
 * several equivalent ways:
 *
 * <p>This one is fully explicit, and is the "base" way that the other
 * mechanisms are converted to:
 *
 * <pre>
 * [ { listenOn: "model",
 *     prop: "firstname",
 *     event: "change:firstname",
 *     func: model => ({
 *       firstname: model.get("firstname")
 *     })
 *   }
 * ]</pre>
 *
 * <p>This one uses get instead of func to reduce a little noise, setting the
 * value into the named prop implicitly. Similarly, you can use a value key to
 * supply a literal value for a prop.
 *
 * <pre>
 * [ { listenOn: "model",
 *     prop: "firstname",
 *     event: "change:firstname",
 *     get: model =>
 *       model.get("firstname")
 *   }
 * ]</pre>
 *
 * <p>By naming an attribute, the prop will be set the same, the change event
 * for the attribute will be used, and the function to do it will be generated.
 *
 * <pre>
 * [ { listenOn: "model",
 *     attribute: "firstname"
 *   }
 * ]</pre>
 *
 * <p>This one uses an object, instead of an array, where the object's keys are
 * props to set. This time, the attribute is set to the prop (reverse of
 * above), but the rest is the same. An explicit attribute is supported, if
 * it's different than the prop.
 *
 * <pre>
 * { firstname: { listenOn: "model" }
 * }</pre>
 *
 * <p>With an object config, you can also pass through raw functions in
 * addition to bindings:
 *
 * <pre>
 * { firstname: { listenOn: "model" },
 *   toggleThinger: (arg1, arg2) => ... do some stuff
 * }</pre>
 *
 * <p>Finally, you may pass a function as a bind config. It will be invoked
 * once (and only once!) when the wrapped component is created, and will be
 * passed the component's props. It must return an object or array config as
 * detailed above. This is primarily of interest for model access in your
 * handlers, but can be used to dynamically build config as well. Just keep
 * in mind that it only gets invoked once!
 *
 * <pre>
 * props => ({
 *   firstname: { listenOn: "model" },
 *   toggleThinger: (arg1, arg2) => ... do some stuff using props.model
 * })</pre>
 *
 * <p>If you provide a `get` or `func` function, it will be passed all the
 * arguments the backbone callback will receive, not just model. This is
 * primarily of interest when listening for add/remove events on a Collection,
 * as they pass the collection itself as the second arg (the model that was
 * added or removed is the first).
 *
 * <p>Getting an attribute from a model may throw an error. If you wish, the adapter
 * can catch these errors and instead pass in a default error value. For example:
 *
 * <pre>
 * { firstname: { listenOn: "model", errorValue: "unknown name"} }
 * </pre>
 *
 * <p>So if model.get("firstname") throws an exception, the prop firstname will
 * be set to "unknown name". Note that `errorValue` is only supported for items
 * which don't use `value` or `get`, and rely on the implicit `func`.
 *
 * <p>When the adapted component mounts, props will be initialized if the
 * binding meets any of these criteria:
 *
 * <ul>
 *     <li>has a `value` key,
 *     <li>has a `get` function,
 *     <li>uses a "change:xxx" event (implicitly or explicitly), OR
 *     <li>listens for "add", "remove", or "destroy" on a Collection
 * </ul>
 *
 * <p>All bindings, regardless of type, will be wired up to monitor changes, and
 * then unwired on unmount. Bindings will also be transparently moved to a new
 * model, if one is set into the wrapping component's props.
 *
 * @param {(array|object|function)} bindConfig - An Backbone bind config; see above.
 * @returns {function} A function that takes a React Component and returns a new React Component.
 */
export function backboneConnect(bindConfig) {
    return WrappedComponent => {
        class BackboneConnect extends React.Component {
            constructor(props) {
                super(props);
                const config = processAdapterConfig(this, bindConfig);
                this.state = config.statics;
                this.listenOns = {};
                config.forEach(c => {
                    const lo = c.listenOn;
                    if (!this.listenOns.hasOwnProperty(lo)) {
                        this.listenOns[lo] = [c];
                    } else {
                        this.listenOns[lo].push(c);
                    }
                });
            }

            UNSAFE_componentWillMount() {
                this.doBackboneInitialization();
            }

            UNSAFE_componentWillReceiveProps(nextProps) {
                for (const listenOn in this.listenOns) {
                    if (this.props[listenOn] !== nextProps[listenOn]) {
                        this.doBackboneListeners("off", listenOn, this.props);
                        this.doBackboneListeners("on", listenOn, nextProps);
                        this.doBackboneInitialization(listenOn, nextProps);
                    }
                }
            }

            componentDidMount() {
                this.doBackboneListeners("on");
            }

            componentWillUnmount() {
                this.doBackboneListeners("off");
            }

            doBackboneListeners(method, listeningOn = null, props = this.props) {
                for (const listenOn in this.listenOns) {
                    if (listeningOn != null && listeningOn !== listenOn) continue;
                    this.listenOns[listenOn].forEach(({event, listenerFunc}) => {
                        if (isListenable(props[listenOn])) {
                            props[listenOn][method](event, listenerFunc, this);
                        }
                    });
                }
            }

            doBackboneInitialization(listeningOn = null, props = this.props) {
                for (const listenOn in this.listenOns) {
                    if (listeningOn != null && listeningOn !== listenOn) continue;
                    this.listenOns[listenOn].forEach(c => {
                        const {func, event} = c;
                        if (isListenable(props[listenOn])) {
                            if ("change:" === event.substr(0, 7) || c.hasOwnProperty("value") || c.hasOwnProperty("get")) {
                                // add/remove/destroy collection events pass the collection as the _second_ arg
                                if (props[listenOn] instanceof Backbone.Collection && (event.indexOf("add") >= 0 || event.indexOf("remove") >= 0 || event.indexOf("destroy") >= 0)) {
                                    this.setState(func(null, props[listenOn]));
                                } else {
                                    // still might be a collection, but it's not a model-centric collection event
                                    this.setState(func(props[listenOn]));
                                }
                            }
                        } else if (process.env.NODE_ENV !== "production") {
                            const avail = Object.keys(props)
                                .filter(k => isListenable(props[k]))
                                .map(it => `'${it}'`);
                            console.error(`adapter listenOn set to '${listenOn}', but only ${avail.join(", ")} ${avail.length === 1 ? "is" : "are"} available`);
                        }
                    });
                }
            }

            render() {
                return <WrappedComponent {...this.props} {...this.state} />;
            }
        }
        BackboneConnect.displayName = `Connect(${WrappedComponent.displayName || WrappedComponent.name || "Component"})`;
        return BackboneConnect;
    };
}

const isListenable = (it) => {
    const instances = [Backbone.Model, Backbone.Collection];

    return instances.some(x =>
        it instanceof x
    );
};



// WEBPACK FOOTER //
// ./app/lib/common/hoc/BackboneReact/backboneConnect.js